import React from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

function TermsPage() {
  const navigate = useNavigate();

  return (
    <motion.div
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.25 }}
      className="min-h-screen pt-24 pb-16"
    >
      <div className="max-w-2xl mx-auto px-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back</span>
        </button>

        <h1 className="text-3xl sm:text-4xl font-serif font-medium text-foreground mb-2">Terms & Privacy</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: 5 April 2026</p>

        {/* Terms of Use */}
        <section className="mb-12">
          <h2 className="text-xl font-serif font-medium text-foreground mb-4">Terms of Use</h2>

          <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
            <div>
              <h3 className="text-foreground font-medium mb-1.5">What Noyo Stories is</h3>
              <p>
                Noyo Stories is a journaling and story-keeping app that helps you record, organise, and share personal stories. By using Noyo Stories, you agree to these terms.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Your content</h3>
              <p>
                You own everything you create on Noyo Stories — your journal entries, stories, audio recordings, and images. We don't claim any ownership of your content. You grant us permission to store and process your content only to provide the service to you.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Acceptable use</h3>
              <p>
                Don't use Noyo Stories to store or share illegal content, harass others, or violate anyone's rights. We may remove content or suspend accounts that violate these terms.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Shared folders</h3>
              <p>
                When you share a folder, collaborators can view and add stories to it. You control who has access and can remove collaborators at any time. Folder owners can manage sharing permissions.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Guest accounts</h3>
              <p>
                Guest accounts are temporary and expire after 7 days. To keep your stories permanently, upgrade to a full account with an email and password.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Service availability</h3>
              <p>
                We do our best to keep Noyo Stories running smoothly, but we can't guarantee 100% uptime. We may update, change, or discontinue features with notice where possible.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Limitation of liability</h3>
              <p>
                Noyo Stories is provided "as is." To the extent permitted by New Zealand law, we are not liable for any indirect, incidental, or consequential damages arising from your use of the service.
              </p>
            </div>
          </div>
        </section>

        {/* Privacy Policy */}
        <section className="mb-12">
          <h2 className="text-xl font-serif font-medium text-foreground mb-4">Privacy Policy</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6">
            This policy explains what data we collect, how we use it, and your rights under the New Zealand Privacy Act 2020.
          </p>

          <div className="space-y-6 text-sm text-muted-foreground leading-relaxed">
            <div>
              <h3 className="text-foreground font-medium mb-1.5">What we collect</h3>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Your name and email address (for account creation)</li>
                <li>Password (stored securely hashed, never in plain text)</li>
                <li>Journal entries, stories, and folder data you create</li>
                <li>Audio recordings you make for voice notes</li>
                <li>Images you upload</li>
                <li>Session cookies (to keep you logged in)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">How we use your data</h3>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>To provide the Noyo Stories service to you</li>
                <li>To transcribe your audio recordings into text (via AI)</li>
                <li>To automatically sort and organise your stories into folders (via AI)</li>
                <li>To send you verification emails when you sign up</li>
              </ul>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Third-party services</h3>
              <p className="mb-2">We use the following services to run Noyo Stories. Your data may be processed by them as part of providing the service:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li><span className="text-foreground">Groq</span> — transcribes your audio recordings</li>
                <li><span className="text-foreground">Google Cloud Storage</span> — stores your images and audio files</li>
                <li><span className="text-foreground">MongoDB</span> — stores your account and story data</li>
                <li><span className="text-foreground">Vercel</span> — hosts the website</li>
                <li><span className="text-foreground">Railway</span> — hosts the backend server</li>
                <li><span className="text-foreground">Resend</span> — sends verification emails</li>
              </ul>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">We will never</h3>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Sell your data to anyone</li>
                <li>Share your data with advertisers</li>
                <li>Use your content for AI training purposes</li>
                <li>Access your stories unless required for technical support you request</li>
              </ul>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Data storage & security</h3>
              <p>
                Your data is stored on secure cloud servers. Passwords are hashed using industry-standard encryption. Session tokens are stored in httpOnly cookies. We take reasonable steps to protect your data, but no system is 100% secure.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Your rights</h3>
              <p className="mb-2">Under the New Zealand Privacy Act 2020, you have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-1">
                <li>Access all personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of all your data</li>
                <li>Export your data</li>
              </ul>
              <p className="mt-2">
                You can delete your account and all associated data at any time from Settings. This action is permanent and cannot be undone.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Children</h3>
              <p>
                Noyo Stories is not intended for children under 13. We do not knowingly collect data from children under 13. If you believe a child has created an account, please contact us so we can remove it.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Changes to this policy</h3>
              <p>
                We may update these terms and privacy policy from time to time. If we make significant changes, we'll notify you through the app.
              </p>
            </div>

            <div>
              <h3 className="text-foreground font-medium mb-1.5">Contact</h3>
              <p>
                If you have any questions about your privacy or these terms, contact us at <a href="mailto:support@noyostories.app" className="text-primary hover:underline">support@noyostories.app</a>.
              </p>
            </div>
          </div>
        </section>

        <div className="border-t border-border pt-6 text-center">
          <p className="text-xs text-muted-foreground">Noyo Stories &middot; New Zealand</p>
        </div>
      </div>
    </motion.div>
  );
}

export default TermsPage;
